import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { MFOItem } from "@/types/kanban";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ChevronLeft, ChevronRight, Users, Calendar, CheckCircle2, AlertTriangle, Clock, Maximize2, Minimize2 } from "lucide-react";

const QUARTERS = [
  { label: "All", value: "all" },
  { label: "Q1", value: "q1" },
  { label: "Q2", value: "q2" },
  { label: "Q3", value: "q3" },
  { label: "Q4", value: "q4" },
];

const getQuarter = (timeline: any) => {
  if (timeline.q1) return "q1";
  if (timeline.q2) return "q2";
  if (timeline.q3) return "q3";
  if (timeline.q4) return "q4";
  return "all";
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const MFOPresentation = () => {
  const [mfos, setMFOs] = useState<MFOItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [quarter, setQuarter] = useState("all");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const navigate = useNavigate();
  const presRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchMFOs = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("mfo_items")
        .select(`
          id, code, title, budget, priority, due_date, status, progress, q1, q2, q3, q4, department_id,
          departments (id, name),
          mfo_success_indicators (indicator),
          mfo_assignees (employee:employees (id, name, avatar)),
          mfo_subtasks (id, text, completed)
        `);
      if (error) {
        setLoading(false);
        return;
      }
      const processed = data.map((item) => ({
        id: item.id,
        code: item.code,
        title: item.title,
        budget: Number(item.budget),
        priority: item.priority,
        status: item.status,
        dueDate: item.due_date,
        progress: item.progress || 0,
        departmentId: item.department_id,
        departmentName: item.departments?.name,
        timeline: {
          q1: item.q1,
          q2: item.q2,
          q3: item.q3,
          q4: item.q4,
        },
        successIndicators: item.mfo_success_indicators?.map(si => si.indicator) || [],
        collaborators: item.mfo_assignees?.map(a => ({
          id: a.employee?.id,
          name: a.employee?.name,
          avatar: a.employee?.avatar
        })) || [],
        subTasks: item.mfo_subtasks || [],
      }));
      setMFOs(processed);
      setLoading(false);
    };
    fetchMFOs();
  }, []);

  // Filter by quarter
  const filteredMFOs = useMemo(() => {
    if (quarter === "all") return mfos;
    return mfos.filter(mfo => mfo.timeline && mfo.timeline[quarter]);
  }, [mfos, quarter]);

  // Slide navigation
  const goNext = useCallback(() => setCurrent((c) => Math.min(filteredMFOs.length - 1, c + 1)), [filteredMFOs.length]);
  const goPrev = useCallback(() => setCurrent((c) => Math.max(0, c - 1)), []);

  // Fullscreen logic
  const toggleFullscreen = () => {
    if (!isFullscreen && presRef.current) {
      presRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else if (isFullscreen && document.fullscreenElement) {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "Escape" && isFullscreen) toggleFullscreen();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev, isFullscreen]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!filteredMFOs.length) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-3xl font-bold mb-4">No MFOs to present</h2>
        <Button onClick={() => navigate("/dashboard/mfo")}>Go to MFOs</Button>
      </div>
    );
  }

  const mfo = filteredMFOs[current];

  return (
    <div
      ref={presRef}
      className="min-h-screen w-full flex flex-col items-center justify-center relative overflow-hidden bg-gradient-to-br from-blue-100/60 via-fuchsia-100/60 to-emerald-100/60 dark:from-[#232946] dark:via-[#1a1a2e] dark:to-[#0f3460] transition-colors duration-300"
    >
      {/* Animated background shapes */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-32 right-0 w-96 h-96 bg-fuchsia-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-emerald-400/20 rounded-full blur-2xl animate-pulse" style={{ transform: 'translate(-50%, -50%)' }} />
      </div>

      {/* Fixed Header with Controls */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border shadow-lg">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          {/* Quarter Filter */}
          <div className="flex gap-2">
            {QUARTERS.map(q => (
              <Button
                key={q.value}
                variant={quarter === q.value ? "default" : "outline"}
                onClick={() => { setQuarter(q.value); setCurrent(0); }}
                className="rounded-full px-4 text-lg shadow"
              >
                {q.label}
              </Button>
            ))}
          </div>

          {/* Fullscreen button */}
          <Button
            onClick={toggleFullscreen}
            variant="ghost"
            size="icon"
            className="bg-background/70 hover:bg-background/90 shadow-lg"
            aria-label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            {isFullscreen ? <Minimize2 className="h-6 w-6" /> : <Maximize2 className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {/* Slide Navigation */}
      <div className="fixed top-1/2 left-4 -translate-y-1/2 z-40">
        <Button variant="ghost" size="icon" onClick={goPrev} disabled={current === 0} className="h-16 w-16 text-3xl bg-background/70 hover:bg-background/90 shadow-lg">
          <ChevronLeft className="h-10 w-10" />
        </Button>
      </div>
      <div className="fixed top-1/2 right-4 -translate-y-1/2 z-40">
        <Button variant="ghost" size="icon" onClick={goNext} disabled={current === filteredMFOs.length - 1} className="h-16 w-16 text-3xl bg-background/70 hover:bg-background/90 shadow-lg">
          <ChevronRight className="h-10 w-10" />
        </Button>
      </div>

      {/* Slide Content - Now Scrollable */}
      <div className="w-full max-w-4xl mt-20 mb-8 mx-4 bg-card/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-border z-10">
        <div className="max-h-[calc(100vh-8rem)] overflow-y-auto p-8 flex flex-col items-center">
          <div className="flex items-center gap-4 mb-4">
            <Badge variant="outline" className="text-lg px-4 py-2 bg-background/80 shadow">{mfo.code}</Badge>
            <h1 className="text-3xl font-bold tracking-tight text-center drop-shadow-lg">{mfo.title}</h1>
          </div>
          <div className="flex flex-wrap gap-3 mb-6 justify-center">
            <Badge variant="secondary" className="text-base px-3 py-1">{mfo.departmentName || mfo.departmentId}</Badge>
            <Badge variant="secondary" className="text-base px-3 py-1">Due: {formatDate(mfo.dueDate)}</Badge>
            <Badge variant="secondary" className="text-base px-3 py-1 capitalize">{mfo.status.replace("-", " ")}</Badge>
            <Badge variant="secondary" className="text-base px-3 py-1">Priority: {mfo.priority}</Badge>
            <Badge variant="secondary" className="text-base px-3 py-1">Budget: ₱{mfo.budget?.toLocaleString()}</Badge>
          </div>
          <div className="w-full flex flex-col items-center mb-6">
            <div className="w-full max-w-lg">
              <Progress value={mfo.progress} className="h-6 rounded-full transition-all duration-700" />
            </div>
            <div className="text-lg font-medium mt-2">Progress: {mfo.progress}%</div>
          </div>
          <div className="w-full mb-6">
            <h2 className="text-xl font-semibold mb-3">Subtasks</h2>
            <ul className="space-y-2">
              {mfo.subTasks && mfo.subTasks.length > 0 ? (
                mfo.subTasks.map((task: any) => (
                  <li key={task.id} className="flex items-center gap-3 text-base">
                    {task.completed ? (
                      <CheckCircle2 className="text-green-500 h-5 w-5" />
                    ) : (
                      <Clock className="text-gray-400 h-5 w-5" />
                    )}
                    <span className={task.completed ? "line-through text-muted-foreground" : ""}>{task.text}</span>
                  </li>
                ))
              ) : (
                <li className="text-muted-foreground text-base">No subtasks</li>
              )}
            </ul>
          </div>
          <div className="w-full mb-6">
            <h2 className="text-xl font-semibold mb-3">Success Indicators</h2>
            <ul className="flex flex-wrap gap-2">
              {mfo.successIndicators && mfo.successIndicators.length > 0 ? (
                mfo.successIndicators.map((si, idx) => (
                  <li key={idx} className="bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm font-medium shadow">
                    {si}
                  </li>
                ))
              ) : (
                <li className="text-muted-foreground text-base">No success indicators</li>
              )}
            </ul>
          </div>
          <div className="w-full mb-4">
            <h2 className="text-xl font-semibold mb-3">Collaborators</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {mfo.collaborators && mfo.collaborators.length > 0 ? (
                mfo.collaborators.map((collab) => (
                  <div key={collab.id} className="flex flex-col items-center gap-1">
                    <Avatar className="h-12 w-12 border-2 border-primary shadow">
                      {collab.avatar ? (
                        <AvatarImage src={collab.avatar} alt={collab.name} />
                      ) : (
                        <AvatarFallback>{collab.name?.split(" ").map(n => n[0]).join("")}</AvatarFallback>
                      )}
                    </Avatar>
                    <span className="text-sm font-medium text-center max-w-[5rem] truncate">{collab.name}</span>
                  </div>
                ))
              ) : (
                <span className="text-muted-foreground text-base">No collaborators</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(mfo.dueDate)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFOPresentation; 